import React from 'react'

import {
  truncateText,
  wrapText,
  X_AXIS_OVERFLOW_TRUNCATE_CHARACTER_LENGTH,
  Y_AXIS_OVERFLOW_TRUNCATE_CHARACTER_LENGTH,
} from 'features/charts-v2/utils/axis-tick-utils'

/**
 * Custom X-axis tick component with overflow support for angled labels
 */
export const CustomXAxisTick = React.memo<{
  x: number
  y: number
  payload: { value: string }
  fontSizePx?: number
  overflowMode?: 'expand' | 'wrap' | 'truncate'
  maxWidth?: number
}>(
  ({
    x,
    y,
    payload,
    fontSizePx = 13,
    overflowMode = 'expand',
    maxWidth = 100,
  }) => {
    let displayText: string | string[] = payload.value

    // Apply overflow logic
    if (overflowMode === 'truncate') {
      displayText = truncateText(
        payload.value,
        X_AXIS_OVERFLOW_TRUNCATE_CHARACTER_LENGTH,
      )
    } else if (overflowMode === 'wrap') {
      displayText = wrapText(payload.value, maxWidth, fontSizePx)
    }

    // Render single line (expand or truncate modes)
    if (typeof displayText === 'string') {
      return (
        <g transform={`translate(${x},${y})`}>
          <text
            x={0}
            y={0}
            dy={13}
            textAnchor="end"
            fill="#666"
            fontSize={fontSizePx}
            transform="rotate(-45)"
            style={{
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            }}
          >
            {displayText}
          </text>
        </g>
      )
    }

    // Render multiple lines (wrap mode)
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          textAnchor="end"
          fill="#666"
          fontSize={fontSizePx}
          transform="rotate(-45)"
          style={{
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          {displayText.map((line, index) => (
            <tspan key={index} x={0} dy={index === 0 ? 13 : fontSizePx + 2}>
              {line}
            </tspan>
          ))}
        </text>
      </g>
    )
  },
)

CustomXAxisTick.displayName = 'CustomXAxisTick'

/**
 * Custom Y-axis tick component with overflow support
 * Memoized to prevent unnecessary re-renders when hovering
 *
 * @param truncateAtChars - Override character limit for truncate mode (default: Y_AXIS_OVERFLOW_TRUNCATE_CHARACTER_LENGTH)
 * @param wrapAtChars - Override pixel width for wrap mode (default: 150)
 */
export const CustomYAxisTick = React.memo<{
  x: number
  y: number
  payload: { value: string }
  fontSizePx?: number
  overflowMode?: 'expand' | 'wrap' | 'truncate'
  truncateAtChars?: number
  wrapAtPx?: number
  /** This tick's position among all category rows — used with `step` to fade
   *  thinned-out labels instead of Recharts mounting/unmounting them. */
  index?: number
  /** Label thinning factor from `useYAxisLabelStep`; every `step`th label is
   *  shown. Defaults to 1 (no thinning). */
  step?: number
}>(
  ({
    x,
    y,
    payload,
    fontSizePx = 13,
    overflowMode = 'expand',
    truncateAtChars = Y_AXIS_OVERFLOW_TRUNCATE_CHARACTER_LENGTH,
    wrapAtPx = 150,
    index,
    step = 1,
  }) => {
    let displayText: string | string[] = String(payload.value)

    // Apply overflow logic
    if (overflowMode === 'truncate') {
      displayText = truncateText(String(payload.value), truncateAtChars)
    } else if (overflowMode === 'wrap') {
      displayText = wrapText(String(payload.value), wrapAtPx, fontSizePx)
    }

    // Fade thinned-out labels rather than Recharts mounting/unmounting them
    // (interval={0} keeps every tick mounted at its normal position; we
    // decide visibility here so a plain CSS transition has something to
    // animate).
    const isVisible = step <= 1 || (index ?? 0) % step === 0
    const tickStyle = {
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      opacity: isVisible ? 1 : 0,
      transition: 'opacity 150ms',
    }

    // Render single line (expand or truncate modes)
    if (typeof displayText === 'string') {
      return (
        <text
          x={x}
          y={y}
          dy={4}
          textAnchor="end"
          fill="#666"
          fontSize={fontSizePx}
          style={tickStyle}
        >
          {displayText}
        </text>
      )
    }

    // Render multiple lines (wrap mode)
    return (
      <text
        x={x}
        y={y}
        textAnchor="end"
        fill="#666"
        fontSize={fontSizePx}
        style={tickStyle}
      >
        {displayText.map((line, lineIndex) => (
          <tspan
            key={lineIndex}
            x={x}
            dy={lineIndex === 0 ? 4 : fontSizePx + 2}
          >
            {line}
          </tspan>
        ))}
      </text>
    )
  },
)

CustomYAxisTick.displayName = 'CustomYAxisTick'
